import React, { useId } from 'react';

// Isometric projection constants
const COS30 = Math.cos(Math.PI / 6);
const SIN30 = Math.sin(Math.PI / 6);

// Building dimensions (3D units)
export const BW = 140; // width
export const BD = 80;  // depth
export const FH = 26;  // floor height

export function toIso(x, y, z) {
  return {
    x: (x - z) * COS30,
    y: -((x + z) * SIN30 + y),
  };
}

function pts(points) {
  return points.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
}

export function buildViewBox(floorCount) {
  const maxH = floorCount * FH;
  const corners = [
    toIso(0, 0, 0), toIso(BW, 0, 0), toIso(0, 0, BD), toIso(BW, 0, BD),
    toIso(0, maxH, 0), toIso(BW, maxH, 0), toIso(0, maxH, BD), toIso(BW, maxH, BD),
  ];
  const xs = corners.map(c => c.x);
  const ys = corners.map(c => c.y);
  const pad = 20;
  const minX = Math.min(...xs) - pad;
  const minY = Math.min(...ys) - pad;
  const w = Math.max(...xs) - minX + pad;
  const h = Math.max(...ys) - minY + pad;
  return { viewBox: `${minX} ${minY} ${w} ${h}`, svgH: h };
}

// Number of window columns / rows per floor
const V_LINES = 7;  // vertical divisions on front face
const D_LINES = 4;  // vertical divisions on right face
const H_LINES = 2;  // horizontal divisions within each floor (window rows)

function FloorBox({ floorIdx, isSelected, onClick, uid }) {
  const yBot = floorIdx * FH;
  const yTop = (floorIdx + 1) * FH;

  const topFace   = [toIso(0, yTop, 0), toIso(BW, yTop, 0), toIso(BW, yTop, BD), toIso(0, yTop, BD)];
  const frontFace = [toIso(0, yTop, 0), toIso(BW, yTop, 0), toIso(BW, yBot, 0), toIso(0, yBot, 0)];
  const rightFace = [toIso(BW, yTop, 0), toIso(BW, yTop, BD), toIso(BW, yBot, BD), toIso(BW, yBot, 0)];

  const labelPos = toIso(BW / 2, yBot + FH / 2, 0);

  // Color scheme: green default, blue when selected
  const topColor   = isSelected ? '#60a5fa' : '#4ade80';
  const frontColor = isSelected ? 'var(--color-primary)' : '#16a34a';
  const rightColor = isSelected ? '#1d4ed8' : '#15803d';
  const outerStroke   = isSelected ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.25)';
  const gridStroke = isSelected ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.22)';

  const clipFront = `cf-${uid}-${floorIdx}`;
  const clipRight = `cr-${uid}-${floorIdx}`;

  // Front face: vertical lines (constant x) + horizontal lines (constant y)
  const frontLines = [];
  for (let h = 1; h <= H_LINES; h++) {
    const y = yBot + (FH * h) / (H_LINES + 1);
    const a = toIso(0, y, 0), b = toIso(BW, y, 0);
    frontLines.push(<line key={`fh${h}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={gridStroke} strokeWidth="0.7" />);
  }
  for (let v = 1; v <= V_LINES; v++) {
    const x = (BW * v) / (V_LINES + 1);
    const a = toIso(x, yTop, 0), b = toIso(x, yBot, 0);
    frontLines.push(<line key={`fv${v}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={gridStroke} strokeWidth="0.7" />);
  }

  // Right face: depth lines (constant z) + horizontal lines (constant y)
  const rightLines = [];
  for (let h = 1; h <= H_LINES; h++) {
    const y = yBot + (FH * h) / (H_LINES + 1);
    const a = toIso(BW, y, 0), b = toIso(BW, y, BD);
    rightLines.push(<line key={`rh${h}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={gridStroke} strokeWidth="0.7" />);
  }
  for (let d = 1; d <= D_LINES; d++) {
    const z = (BD * d) / (D_LINES + 1);
    const a = toIso(BW, yTop, z), b = toIso(BW, yBot, z);
    rightLines.push(<line key={`rd${d}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={gridStroke} strokeWidth="0.7" />);
  }

  return (
    <g onClick={onClick} style={{ cursor: 'pointer' }}>
      <defs>
        <clipPath id={clipFront}><polygon points={pts(frontFace)} /></clipPath>
        <clipPath id={clipRight}><polygon points={pts(rightFace)} /></clipPath>
      </defs>

      {/* Right face */}
      <polygon points={pts(rightFace)} fill={rightColor} stroke={outerStroke} strokeWidth="0.8" />
      <g clipPath={`url(#${clipRight})`}>{rightLines}</g>

      {/* Front face */}
      <polygon points={pts(frontFace)} fill={frontColor} stroke={outerStroke} strokeWidth="0.8" />
      <g clipPath={`url(#${clipFront})`}>{frontLines}</g>

      {/* Top face */}
      <polygon points={pts(topFace)} fill={topColor} stroke={outerStroke} strokeWidth="0.8" />

      {/* Floor label */}
      <text
        x={labelPos.x}
        y={labelPos.y}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="white"
        fontSize="8.5"
        fontWeight="700"
        opacity="0.95"
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {floorIdx + 1}
      </text>
    </g>
  );
}

/**
 * IsometricBuilding
 *
 * Props:
 *   floorCount    {number}       – number of floors to render
 *   selectedFloor {number|null}  – zero-based index of the selected floor, or null
 *   onFloorClick  {function}     – called with floor index (or null to deselect)
 *   style         {object}       – optional style overrides for the <svg> element
 */
export default function IsometricBuilding({ floorCount = 5, selectedFloor = null, onFloorClick, style }) {
  const uid = useId().replace(/:/g, '');
  const { viewBox } = buildViewBox(floorCount);

  return (
    <svg
      viewBox={viewBox}
      style={{ width: '100%', maxWidth: '340px', height: 'auto', ...style }}
    >
      {Array.from({ length: floorCount }, (_, i) => (
        <FloorBox
          key={i}
          floorIdx={i}
          isSelected={selectedFloor === i}
          onClick={() => onFloorClick?.(selectedFloor === i ? null : i)}
          uid={uid}
        />
      ))}
    </svg>
  );
}
